import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, Stack, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUserContext } from '@/contexts/userContext';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/errorMessage';
import DataGridTable from '@/components/datagridTable';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import AddAccessControlDialog from '@/components/entities/administration/accessControl/addAccessControlDialog';
import ViewAccessDialog from '@/components/entities/administration/accessControl/viewAccessDialog';
import { useConfirm } from 'material-ui-confirm';
import { format } from 'date-fns';

type Props = {
  entity: any;
  maxHeight?: number;
};

const accessCache: Record<string, { users: any[]; invites: any[] }> = {};
const fetchGuard: Record<string, boolean> = {};

const EntityAccessControl = ({ entity, maxHeight }: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const apiContext = useApiContext();
  const confirm = useConfirm();

  const getManagePermission = () => {
    switch (entity?.entityType) {
      case 'dataSourceInstance':
        return 'pipeline:dataSources:accessControl:manage';
      case 'agentInstance':
        return 'pipeline:agents:accessControl:manage';
      case 'userAppInstance':
        return 'pipeline:apps:accessControl:manage';
      case 'pipelineInstance':
        return 'pipeline:accessControl:manage';
      default:
        return `${entity?.entityType}:accessControl:manage`;
    }
  };

  const canAddUser = hasPermission(getManagePermission(), entity?.userAccess);
  const canDeleteUser = hasPermission(getManagePermission(), entity?.userAccess);

  const [loading, setLoading] = useState<boolean>(!accessCache[entity?.id]);
  const [loadUsers, setLoadUsers] = useState(true);

  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [openAccessDialog, setOpenAccessDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [entityUsers, setEntityUsers] = useState<any[]>(accessCache[entity?.id]?.users || []);
  const [entityUserInvites, setEntityUserInvites] = useState<any[]>(accessCache[entity?.id]?.invites || []);
  const [error, setError] = useState<any>();

  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1 || 'yyyy-MM-dd HH:mm:ss';

  useEffect(() => {
    const getEntityUsers = async () => {
      if (!entity?.id || fetchGuard[entity.id]) return;
      if (!loadUsers && accessCache[entity.id]) return;

      fetchGuard[entity.id] = true;
      setError(null);

      if (!accessCache[entity.id] || loadUsers) {
        setLoading(true);
      }

      try {
        const users = await apiContext.getEntityUsers(entity.entityType, entity.entityDefId, entity.id);
        const invites = await apiContext.getEntityUserInvites(entity.entityType, entity.entityDefId, entity.id);

        const updatedUsers = (users || []).map((u: any) => ({ ...u, id: u.userId }));
        accessCache[entity.id] = { users: updatedUsers, invites: invites || [] };

        setEntityUsers(updatedUsers);
        setEntityUserInvites(invites || []);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
        fetchGuard[entity.id] = false;
        setLoadUsers(false);
      }
    };

    if (entity?.id) {
      getEntityUsers();
    }
  }, [entity.id, entity.entityType, entity.entityDefId, loadUsers]);

  const deleteEntityUser = (user: any) => {
    confirm({ description: t('entity.administration.accessControl.users.confirmDelete', { user: user.email }) }).then(
      async () => {
        setError(null);
        try {
          setLoading(true);
          await apiContext.deleteEntityUser(entity.entityType, entity.entityDefId, entity.id, user.id);
          const updatedUsers = entityUsers.filter((u: any) => u.id !== user.id);
          setEntityUsers(updatedUsers);
          if (accessCache[entity.id]) {
            accessCache[entity.id].users = updatedUsers;
          }
        } catch (error) {
          setError(error);
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const deleteUserInvite = (request: any) => {
    confirm({
      description: t('entity.administration.accessControl.userInvites.confirmDelete', { user: request.userEmail }),
    }).then(async () => {
      setError(null);
      try {
        setLoading(true);
        await apiContext.deleteEntityUserInvite(entity.entityType, entity.entityDefId, entity.id, request.id);
        const updatedInvites = entityUserInvites.filter((r: any) => r.id !== request.id);
        setEntityUserInvites(updatedInvites);
        if (accessCache[entity.id]) {
          accessCache[entity.id].invites = updatedInvites;
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    });
  };

  const showAddUserDialog = () => {
    setOpenAddUserDialog(true);
  };

  const showAccessDialog = (user: any) => {
    setSelectedUser(user);
    setOpenAccessDialog(true);
  };

  const hideAccessDialog = () => {
    setSelectedUser(null);
    setOpenAccessDialog(false);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'userName',
        headerName: t('entity.administration.accessControl.users.columns.userName'),
        flex: 1,
        valueGetter: (value: any, row: any) => {
          return row ? `${row.lastName || ''}, ${row.firstName || ''}` : '';
        },
      },
      {
        field: 'email',
        headerName: t('entity.administration.accessControl.users.columns.email'),
        flex: 1,
      },
      {
        field: 'access',
        headerName: t('entity.administration.accessControl.users.columns.access'),
        width: 200,
        renderCell: (params) => {
          const role = params.row?.userAccess?.role;
          if (role === 'owner') {
            return t(`entity.administration.accessControl.users.roles.${role}`);
          } else if (role || params.row?.userAccess?.permissions) {
            return (
              <Button
                variant="outlined"
                size="small"
                onClick={() => showAccessDialog(params.row)}
                sx={{ textTransform: 'none' }}
              >
                {t('entity.administration.accessControl.users.actions.viewAccess')}
              </Button>
            );
          }
          return '';
        },
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        getActions: (params: any) => {
          const isOwner = params.row?.userAccess?.role === 'owner';
          return isOwner
            ? []
            : canDeleteUser
              ? [
                  <GridActionsCellItem
                    key="delete"
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={() => deleteEntityUser(params.row)}
                  />,
                ]
              : [];
        },
      },
    ],
    [t, canDeleteUser, entityUsers],
  );

  const userInviteColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'userEmail',
        headerName: t('entity.administration.accessControl.userInvites.columns.email'),
        flex: 1,
      },
      {
        field: 'requestedDate',
        headerName: t('entity.administration.accessControl.userInvites.columns.requestedDate'),
        flex: 1,
        valueGetter: (value: any) => {
          if (!value) return '';
          try {
            return format(new Date(value), dataTimeFormat);
          } catch (e) {
            return value;
          }
        },
      },
      {
        field: 'status',
        headerName: t('entity.administration.accessControl.userInvites.columns.status'),
        width: 200,
      },
      {
        field: 'access',
        headerName: t('entity.administration.accessControl.userInvites.columns.access'),
        width: 200,
        renderCell: (params) => {
          const role = params.row?.userAccess?.role;
          if (role === 'owner') {
            return t(`entity.administration.accessControl.userInvites.roles.${role}`);
          } else if (role || params.row?.userAccess?.permissions) {
            return (
              <Button
                variant="outlined"
                size="small"
                onClick={() => showAccessDialog(params.row)}
                sx={{ textTransform: 'none' }}
              >
                {t('entity.administration.accessControl.userInvites.actions.viewAccess')}
              </Button>
            );
          }
          return '';
        },
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        getActions: (params: any) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => deleteUserInvite(params.row)}
          />,
        ],
      },
    ],
    [t, dataTimeFormat, entityUserInvites],
  );

  return (
    <Stack
      justifyContent="flex-start"
      spacing={3}
      sx={{ height: maxHeight ? `${maxHeight}px` : '100%', overflowY: 'auto', overflowX: 'hidden', py: 1, px: 0.5 }}
    >
      {error && <ErrorMessage error={error} />}
      <Card
        sx={{
          border: 1,
          borderColor: 'divider',
          boxShadow: 'none',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <CardHeader
          disableTypography
          title={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('entity.administration.accessControl.users.header')}
            </Typography>
          }
          action={
            canAddUser && (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={showAddUserDialog}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  color: 'text.primary',
                }}
              >
                {t('entity.administration.accessControl.actions.addUser')}
              </Button>
            )
          }
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column' }}>
          {entityUsers.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Alert color="info">{t('entity.administration.accessControl.noUserMessage')}</Alert>
            </Box>
          )}
          {entityUsers.length > 0 && (
            <Box sx={{ minHeight: '400px', width: '100%', overflow: 'hidden' }}>
              <DataGridTable rows={entityUsers} columns={columns} loading={loading} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card
        sx={{
          border: 1,
          borderColor: 'divider',
          boxShadow: 'none',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <CardHeader
          title={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('entity.administration.accessControl.userInvites.header')}
            </Typography>
          }
          disableTypography
          sx={{ pb: 0 }}
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ minHeight: '300px', width: '100%', overflow: 'hidden' }}>
            <DataGridTable rows={entityUserInvites} columns={userInviteColumns} loading={loading} />
          </Box>
        </CardContent>
      </Card>

      <AddAccessControlDialog
        entity={entity}
        existingUsers={entityUsers}
        open={openAddUserDialog}
        onCancel={() => setOpenAddUserDialog(false)}
        onSubmit={() => {
          setOpenAddUserDialog(false);
          setLoadUsers(true);
        }}
      />
      {selectedUser && (
        <ViewAccessDialog entity={entity} user={selectedUser} open={openAccessDialog} onClose={hideAccessDialog} />
      )}
    </Stack>
  );
};

export default EntityAccessControl;
